import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { formatDate, formatEUR } from '../../lib/format';

interface ReturnSummary {
  id: string;
  returnNumber: string | null;
  orderNumber: string;
  customerName: string | null;
  receivedAt: string | null;
  units: number;
}
interface ReturnLine {
  id: string;
  productName: string;
  sku: string | null;
  quantity: number;
  received: number;
}
interface Decision {
  accepted: string;
  refused: string;
  refusalReason: string;
  acceptedCondition: 'RESALEABLE' | 'DEFECTIVE';
  note: string;
}
const REASONS = ['DIFFERENT_PART', 'USED', 'FITTED', 'ELECTRICAL_INSTALLED', 'OTHER'] as const;

/**
 * Inspection (migration 135): no refund before the part is back and CHECKED. Returns received
 * into Zone Q wait here; each unit is accepted (resaleable → shelf, defective → stays in Q for the
 * supplier) or refused with its reason (different part, used, fitted, installed electrical part)
 * and goes back to the customer. The credit note is minted for the accepted units and the
 * refunds follow on the original payment channel.
 */
export default function InspectReturns({ refreshKey }: { refreshKey: number }) {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const [pending, setPending] = useState<ReturnSummary[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [lines, setLines] = useState<ReturnLine[]>([]);
  const [dec, setDec] = useState<Record<string, Decision>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    creditNoteNumber: string | null;
    status: string;
    refunds: Array<{ channel: string; amount: number; status: string }>;
  } | null>(null);

  const load = useCallback(() => {
    apiClient
      .get<{ data: ReturnSummary[] }>('/returns', { params: { status: 'RECEIVED' } })
      .then((r) => setPending(r.data.data))
      .catch(() => setPending([]));
  }, []);
  useEffect(load, [load, refreshKey]);

  const start = async (id: string) => {
    setResult(null);
    try {
      const { data } = await apiClient.get<{ lines: ReturnLine[] }>(`/returns/${id}`);
      const received = data.lines.filter((l) => l.received > 0);
      setLines(received);
      setDec(
        Object.fromEntries(
          received.map((l) => [
            l.id,
            {
              accepted: String(l.received),
              refused: '0',
              refusalReason: '',
              acceptedCondition: 'RESALEABLE',
              note: '',
            },
          ])
        )
      );
      setOpen(id);
    } catch (err) {
      showApiError(err);
    }
  };

  const submit = async () => {
    if (!open) return;
    setBusy(true);
    try {
      await apiClient.post(
        `/returns/${open}/inspect`,
        {
          lines: lines.map((l) => {
            const d = dec[l.id];
            return {
              lineId: l.id,
              accepted: Number(d.accepted) || 0,
              refused: Number(d.refused) || 0,
              refusalReason: Number(d.refused) > 0 ? d.refusalReason || null : null,
              acceptedCondition: Number(d.accepted) > 0 ? d.acceptedCondition : null,
              note: d.note.trim() || null,
            };
          }),
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      const { data } = await apiClient.get<{
        status: string;
        creditNoteNumber: string | null;
        refunds: Array<{ channel: string; amount: number; status: string }>;
      }>(`/returns/${open}`);
      setResult(data);
      push({ tone: 'success', message: t('returns.inspect.done') });
      setOpen(null);
      load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const set = (id: string, k: keyof Decision, v: string) =>
    setDec((x) => ({ ...x, [id]: { ...x[id], [k]: v } }));

  return (
    <section className="border rounded p-4 bg-card space-y-3 text-sm">
      <h2 className="font-semibold">{t('returns.inspect.title')}</h2>
      <p className="text-xs text-muted-foreground">{t('returns.inspect.hint')}</p>

      {result && (
        <div className="rounded border border-green-600/40 bg-green-50 p-3 dark:bg-green-950/30">
          <div>
            {result.creditNoteNumber
              ? t('returns.inspect.creditNote', { number: result.creditNoteNumber })
              : t('returns.inspect.noCredit')}
          </div>
          {result.refunds.length > 0 ? (
            <ul className="mt-1 list-disc pl-5">
              {result.refunds.map((r, i) => (
                <li key={i}>
                  {formatEUR(r.amount)} — {t(`refunds.channel.${r.channel}`)} —{' '}
                  {t(`refunds.status.${r.status}`)}
                </li>
              ))}
            </ul>
          ) : (
            result.creditNoteNumber && <div className="mt-1">{t('returns.inspect.onAccount')}</div>
          )}
          {result.refunds.some((r) => r.status !== 'DONE') && (
            <Link to="/refunds" className="mt-1 inline-block text-primary underline">
              {t('returns.inspect.toRefunds')}
            </Link>
          )}
        </div>
      )}

      {pending.length === 0 && !open && (
        <p className="text-xs text-muted-foreground">{t('returns.inspect.none')}</p>
      )}
      {!open && pending.length > 0 && (
        <ul className="divide-y">
          {pending.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="font-mono">{r.returnNumber ?? r.id.slice(0, 8)}</span>
              <span>{r.customerName ?? '—'}</span>
              <span className="text-muted-foreground">{r.orderNumber}</span>
              <span className="text-xs text-muted-foreground">{formatDate(r.receivedAt)}</span>
              <Button size="sm" className="ml-auto" onClick={() => void start(r.id)}>
                {t('returns.inspect.start')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="space-y-3">
          {lines.map((l) => {
            const d = dec[l.id];
            const sum = (Number(d.accepted) || 0) + (Number(d.refused) || 0);
            return (
              <div key={l.id} className="rounded border p-3">
                <div className="font-medium">
                  {l.productName} <span className="text-xs text-muted-foreground">{l.sku}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('returns.inspect.received', { n: l.received })}
                </div>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs">{t('returns.inspect.accepted')}</span>
                    <Input
                      className="h-8 w-20"
                      type="number"
                      min={0}
                      max={l.received}
                      value={d.accepted}
                      onChange={(e) => set(l.id, 'accepted', e.target.value)}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs">{t('returns.inspect.condition')}</span>
                    <Select
                      className="h-8 w-48"
                      value={d.acceptedCondition}
                      onChange={(e) => set(l.id, 'acceptedCondition', e.target.value)}
                    >
                      <option value="RESALEABLE">{t('returns.inspect.resaleable')}</option>
                      <option value="DEFECTIVE">{t('returns.inspect.defective')}</option>
                    </Select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs">{t('returns.inspect.refused')}</span>
                    <Input
                      className="h-8 w-20"
                      type="number"
                      min={0}
                      max={l.received}
                      value={d.refused}
                      onChange={(e) => set(l.id, 'refused', e.target.value)}
                    />
                  </label>
                  {Number(d.refused) > 0 && (
                    <label className="flex flex-col gap-1">
                      <span className="text-xs">{t('returns.inspect.refusalReason')}</span>
                      <Select
                        className="h-8 w-56"
                        value={d.refusalReason}
                        onChange={(e) => set(l.id, 'refusalReason', e.target.value)}
                      >
                        <option value="">—</option>
                        {REASONS.map((r) => (
                          <option key={r} value={r}>
                            {t(`returns.inspect.reason.${r}`)}
                          </option>
                        ))}
                      </Select>
                    </label>
                  )}
                  <label className="flex min-w-[12rem] flex-1 flex-col gap-1">
                    <span className="text-xs">{t('returns.inspect.note')}</span>
                    <Input
                      className="h-8"
                      value={d.note}
                      onChange={(e) => set(l.id, 'note', e.target.value)}
                    />
                  </label>
                </div>
                {sum !== l.received && (
                  <div className="mt-1 text-xs text-destructive">
                    {t('returns.inspect.mustMatch', { n: l.received })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex gap-2">
            <Button
              disabled={
                busy ||
                lines.some((l) => {
                  const d = dec[l.id];
                  return (
                    (Number(d.accepted) || 0) + (Number(d.refused) || 0) !== l.received ||
                    (Number(d.refused) > 0 && !d.refusalReason)
                  );
                })
              }
              onClick={() => void submit()}
            >
              {t('returns.inspect.submit')}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
